import * as React from "react";
import * as S from "./style";
import { Dialog, Typography, Tab, Box, Chip, CircularProgress } from "@mui/material";
import MarkdownContent from "../MarkdownContent";
import { renderImagePath, groupChunksByPage, highlightChunksInText } from "./helpers";
import { getGuidelineDisplay } from "../requests";
import { Guideline, GuidelineDocWithChunks } from "./types";

interface GuidelineSourcesProps {
  guidelines?: Guideline[];
  guidelineDocs?: GuidelineDocWithChunks[];
  guidelineChunkIds?: string[];
}

export default function GuidelineSources(props: GuidelineSourcesProps) {
  const [open, setOpen] = React.useState(false);
  const [selectedDocTab, setSelectedDocTab] = React.useState(0);
  const [selectedPageTabs, setSelectedPageTabs] = React.useState<Record<number, number>>({});
  const [selectedVirtualPages, setSelectedVirtualPages] = React.useState<Record<number, number>>({});
  const [loadedGuidelines, setLoadedGuidelines] = React.useState<Guideline[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const firstHighlightRef = React.useRef<HTMLDivElement | null>(null);
  const usingDocs = !props.guidelines && props.guidelineDocs && props.guidelineDocs.length > 0;
  const guidelines = usingDocs ? loadedGuidelines : (props.guidelines || []);
  const storedChunkIds = React.useMemo(
    () => Array.from(new Set(props.guidelineChunkIds || [])),
    [props.guidelineChunkIds]
  );

  const groupedByDocument = React.useMemo(() => {
    const groups = new Map<string, Guideline[]>();

    guidelines.forEach(g => {
      const key = g.metadata?.title || g.source_img || 'Unknown';
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(g);
    });

    const result = Array.from(groups.entries()).map(([title, guidelines]) => {
      const guidelineWithMetadata = guidelines.find(g => g.metadata);
      return {
        title,
        guidelines,
        metadata: guidelineWithMetadata?.metadata,
        source_img: guidelineWithMetadata?.source_img || guidelines[0].source_img
      };
    });
    return result;
  }, [guidelines]);

  const displayInfo = React.useMemo(() => {
    if (usingDocs && props.guidelineDocs) {
      const totalChunks = storedChunkIds.length;
      const docsWithImages = props.guidelineDocs.filter(doc => !!doc.image_url);
      const uniqueSources = docsWithImages.slice(0, 3).map(doc => ({
        source_img: doc.image_url!
      }));
      return {
        count: totalChunks,
        uniqueSources,
        hasImages: docsWithImages.length > 0,
      };
    } else {
      const uniqueGuidelineSources = guidelines.filter((g, idx, arr) => {
        return g.source_img && arr.findIndex(x => x.source_img === g.source_img) === idx;
      });
      // Fall back to guidelineDocs image_url if guidelines lack source_img
      const docsWithImages = (props.guidelineDocs || []).filter(doc => !!doc.image_url);
      const hasImages = uniqueGuidelineSources.length > 0 || docsWithImages.length > 0;
      const uniqueSources = uniqueGuidelineSources.length > 0
        ? uniqueGuidelineSources.slice(0, 3)
        : docsWithImages.slice(0, 3).map(doc => ({ source_img: doc.image_url! }));
      return {
        count: guidelines.length,
        uniqueSources,
        hasImages,
      };
    }
  }, [guidelines, props.guidelineDocs, storedChunkIds.length, usingDocs]);

  const handleOpen = async () => {
    setOpen(true);
    if (usingDocs && props.guidelineDocs && loadedGuidelines.length === 0) {
      setIsLoading(true);
      try {
        const documentIds = props.guidelineDocs.map((d) => d.guideline_docs_id);
        const response = await getGuidelineDisplay(documentIds, storedChunkIds);
        const displayDocs = response?.data || [];

        const loadedChunks: Guideline[] = [];
        displayDocs.forEach((docDisplay) => {
          const { document: doc, section: sections } = docDisplay;
          sections.forEach((section) => {
            section.chunks_referenced.forEach((chunk) => {
              loadedChunks.push({
                text: chunk.raw_text || "",
                source_img: doc.image_url || "",
                chunk_id: chunk.guideline_chunk_id,
                page_no: section.page_no,
                section_id: section.guideline_section_id,
                // section_text is what groupChunksByPage + highlightChunksInText use
                // to render the full page body with chunk highlights applied
                section_text: section.raw_text ?? undefined,
                section_raw_text: section.raw_text ?? undefined,
                metadata: {
                  title: doc.title,
                  author: doc.author,
                  version_label: doc.version_label,
                  source_org: doc.source_org,
                  published_date: doc.published_date,
                  jurisdiction: doc.jurisdiction,
                  url: doc.url,
                },
              });
            });
          });
        });
        setLoadedGuidelines(loadedChunks);
      } catch (error) {
        console.error("Failed to load guideline display data:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedDocTab(0);
    setSelectedPageTabs({});
    setSelectedVirtualPages({});
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedDocTab(newValue);
  };

  const renderChunksByPage = (
    guidelines: Guideline[],
    pageNumbers: number[],
    selectedVirtualPage?: number,
    selectedPageIndex?: number
  ) => {
    const elements: React.ReactNode[] = [];

    if (pageNumbers.length === 0) {
      if (selectedVirtualPage !== undefined && guidelines[selectedVirtualPage]) {
        const chunk = guidelines[selectedVirtualPage];
        return (
          <S.SingleChunkContainer>
            {chunk.short_title && (
              <S.ChunkTitle variant="body2">{chunk.short_title}</S.ChunkTitle>
            )}
            <Box ref={firstHighlightRef}>
              <MarkdownContent content={chunk.text} />
            </Box>
          </S.SingleChunkContainer>
        );
      }

      return (
        <>
          {guidelines.map((chunk, idx) => (
            <S.ChunkListItem key={`chunk-${chunk.chunk_id || idx}`}>
              {chunk.short_title && (
                <S.ChunkTitle variant="body2">{chunk.short_title}</S.ChunkTitle>
              )}
              <Box ref={idx === 0 ? firstHighlightRef : null}>
                <MarkdownContent content={chunk.text} />
              </Box>
            </S.ChunkListItem>
          ))}
        </>
      );
    }

    // Group chunks by page for page-level rendering
    const pageViews = groupChunksByPage(guidelines);

    const pagesToRender = selectedPageIndex !== undefined && pageViews[selectedPageIndex] !== undefined
      ? [pageViews[selectedPageIndex]]
      : pageViews;

    pagesToRender.forEach((pageView) => {
      const highlightedText = highlightChunksInText(pageView.section_text, pageView.chunks);
      elements.push(
        <S.PageContainer key={`page-${pageView.page_no}-${pageView.section_id}`}>
          <Box ref={pageView.page_no === pageViews[0]?.page_no ? firstHighlightRef : null}>
            <MarkdownContent content={highlightedText} />
          </Box>
        </S.PageContainer>
      );
    });

    return <>{elements}</>;
  };

  const label =
    displayInfo.uniqueSources.length === 1 ? "1 Source" : `${displayInfo.uniqueSources.length} Sources`;
  return (
    <>
      <S.SourceButton onClick={handleOpen}>
        {displayInfo.hasImages ? (
          <S.AvatarStack>
            {displayInfo.uniqueSources.map((g, idx) => (
              <S.GuidelineImage
                key={g.source_img || idx}
                src={renderImagePath(g.source_img)}
                style={{
                  width: 16,
                  height: 16,
                  marginLeft: idx === 0 ? 0 : -4,
                  zIndex: displayInfo.uniqueSources.length - idx,
                  border: "1px solid white",
                }}
              />
            ))}
          </S.AvatarStack>
        ) : (
          <S.AvatarStack>
            <S.CountSourceAvatar>
              {displayInfo.count}
            </S.CountSourceAvatar>
          </S.AvatarStack>
        )}
        <span>{label}</span>
      </S.SourceButton>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        {isLoading ? (
          <S.LoadingDialogContent>
            <CircularProgress />
          </S.LoadingDialogContent>
        ) : groupedByDocument.length === 0 ? (
          <S.MainDialogContent>
            <Typography color="textSecondary">No guidelines available</Typography>
          </S.MainDialogContent>
        ) : (
          <>
            <S.OcrDisclaimer variant="caption">
              Disclaimer: This text is shown exactly as our model detected it via
              OCR from the source document. It may contain formatting quirks or
              minor detection errors. Always verify against the original source.
            </S.OcrDisclaimer>
            {groupedByDocument.length > 1 && (
              <S.DocumentTabs
                value={selectedDocTab}
                onChange={handleTabChange}
              >
                {groupedByDocument.map((group, idx) => (
                  <Tab
                    key={idx}
                    label={
                      <S.TabLabel>
                        {group.source_img && (
                          <S.TabLogo
                            src={renderImagePath(group.source_img)}
                            alt=""
                          />
                        )}
                        <S.TabText>
                          {group.metadata?.title || `Document ${idx + 1}`}
                        </S.TabText>
                      </S.TabLabel>
                    }
                  />
                ))}
              </S.DocumentTabs>
            )}
            <S.MainDialogContent>
              {groupedByDocument.map((group, groupIdx) => {
                // Get unique pages for this document
                const uniquePages: number[] = Array.from(
                  new Set(group.guidelines.map(g => g.page_no).filter((p): p is number => p !== undefined))
                ).sort((a, b) => a - b);
                const virtualPages = uniquePages.length === 0
                  ? group.guidelines.map((g, idx) => ({
                    index: idx,
                    label: g.page_no ? `Page ${g.page_no}` : `Chunk ${idx + 1}`
                  }))
                  : [];

                return (
                  <S.StyledBox
                    key={groupIdx}
                    hidden={selectedDocTab !== groupIdx}
                    isActive={selectedDocTab === groupIdx}
                  >
                    {selectedDocTab === groupIdx && (
                      <>
                        {group.metadata && (
                          <S.MetadataSection>
                            {group.metadata.title && (
                              <S.MetadataHeader>
                                {group.source_img && (
                                  <S.GuidelineImage src={renderImagePath(group.source_img)} />
                                )}
                                <S.MetadataTitle variant="h6">
                                  {group.metadata.title}
                                </S.MetadataTitle>
                              </S.MetadataHeader>
                            )}
                            <S.MetadataChips>
                              {group.metadata.source_org && (
                                <Chip label={group.metadata.source_org} size="small" color="primary" variant="outlined" />
                              )}
                              {group.metadata.version_label && (
                                <Chip label={`Version ${group.metadata.version_label}`} size="small" variant="outlined" />
                              )}
                              {group.metadata.jurisdiction && (
                                <Chip label={group.metadata.jurisdiction} size="small" variant="outlined" />
                              )}
                            </S.MetadataChips>
                            {group.metadata.author && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                <b>Author</b>: {group.metadata.author}
                              </Typography>
                            )}
                            {group.metadata.published_date && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                <b>Pub</b>: {new Date(group.metadata.published_date).toLocaleDateString()}
                              </Typography>
                            )}
                            {group.metadata.url && (
                              <S.StyledTypography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                              >
                                <b>Link to Source</b>: <S.SourceLink
                                  href={group.metadata.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {group.metadata.url}
                                </S.SourceLink>
                              </S.StyledTypography>
                            )}
                          </S.MetadataSection>
                        )}
                        {uniquePages.length > 1 && (
                          <S.PageTabs
                            value={selectedPageTabs[groupIdx] ?? 0}
                            onChange={(e, val) =>
                              setSelectedPageTabs(prev => ({
                                ...prev,
                                [groupIdx]: val
                              }))
                            }
                          >
                            {uniquePages.map((page) => (
                              <Tab key={page} label={`Page ${page}`} />
                            ))}
                          </S.PageTabs>
                        )}
                        {uniquePages.length === 0 && virtualPages.length > 1 && (
                          <S.PageTabs
                            value={selectedVirtualPages[groupIdx] ?? 0}
                            onChange={(e, val) =>
                              setSelectedVirtualPages(prev => ({
                                ...prev,
                                [groupIdx]: val
                              }))
                            }
                            variant="scrollable"
                            scrollButtons="auto"
                          >
                            {virtualPages.map((page) => (
                              <Tab key={page.index} label={page.label} />
                            ))}
                          </S.PageTabs>
                        )}
                        <S.ScrollableContent>
                          {renderChunksByPage(
                            group.guidelines,
                            uniquePages,
                            uniquePages.length === 0 ? (selectedVirtualPages[groupIdx] ?? 0) : undefined,
                            uniquePages.length > 0 ? (selectedPageTabs[groupIdx] ?? 0) : undefined
                          )}
                        </S.ScrollableContent>
                      </>
                    )}
                  </S.StyledBox>
                );
              })}
            </S.MainDialogContent>
          </>
        )}
      </Dialog>
    </>
  );
}
